<template>
  <div>
    <b-row class="justify-content-between mb-3">
      <b-col class="d-flex align-items-center">
        <h2 v-if="!ignoredCorrespondence" class="mb-0">
          <b-badge variant="warning">Votes par correspondance</b-badge>
        </h2>
        <div v-else class="mb-0 d-inline-flex align-items-center">
          <h4>
            <b-badge variant="warning">
              Votes par correspondance non prise en compte
            </b-badge>
          </h4>
          <slot name="recalculate-vote-btn" />
        </div>
      </b-col>
      <b-col cols="3" sm="6" md="3">
        <label>Rechercher</label>
        <b-form-input v-model="search" placeholder="Tapez pour rechercher" @input="filter" />
      </b-col>
    </b-row>

    <appLockOverlay :condition="showOverlay">
      <template v-slot:lock-overlay-content>
        <b-table
          bordered
          class="agv-table"
          :fields="fields"
          :items="tableData"
          :tbody-tr-class="detailsClassFn"
        >
          <template v-slot:cell(building_owner.lastname)="row">
            <div>
              <appVotingsMandateName
                :current-resolution="currentResolutionData"
                :main-mandate="row.item"
              />
            </div>
          </template>
          <template v-slot:cell(nsp)="row">
            <div>
              <appVotingsMandateVote
                v-if="getTantiemes(row.item) != 0"
                :type="voteTypes.nsp"
                :main-mandate="row.item"
                :current-resolution="currentResolutionData"
                :disabled="disableVoting(row.item.$disabled, getTantiemes(row.item))"
                :show-select-all-represented-votes-button="false"
              />
            </div>
          </template>
          <template v-slot:cell(for)="row">
            <div>
              <appVotingsMandateVote
                v-if="getTantiemes(row.item) != 0"
                :type="voteTypes.for"
                :main-mandate="row.item"
                :current-resolution="currentResolutionData"
                :disabled="disableVoting(row.item.$disabled, getTantiemes(row.item))"
                :show-select-all-represented-votes-button="false"
              />
            </div>
          </template>
          <template v-slot:cell(against)="row">
            <div>
              <appVotingsMandateVote
                v-if="getTantiemes(row.item) != 0"
                :type="voteTypes.against"
                :main-mandate="row.item"
                :current-resolution="currentResolutionData"
                :disabled="disableVoting(row.item.$disabled, getTantiemes(row.item))"
                :show-select-all-represented-votes-button="false"
              />
            </div>
          </template>
          <template v-slot:cell(abs)="row">
            <div>
              <appVotingsMandateVote
                v-if="getTantiemes(row.item) != 0"
                :type="voteTypes.abstain"
                :main-mandate="row.item"
                :current-resolution="currentResolutionData"
                :disabled="disableVoting(row.item.$disabled, getTantiemes(row.item))"
                :show-select-all-represented-votes-button="false"
              />
            </div>
          </template>
        </b-table>
      </template>
    </appLockOverlay>
  </div>
</template>

<script>
import ResolutionVoteORM from "@/store/orm-axios/models/ResolutionVote";
import MeetingCorrespondenceVoteORM from "@/store/orm-axios/models/MeetingCorrespondenceVote";
import MeetingCorrespondenceVoteMW from "@/middlewares/MeetingCorrespondenceVote";
import MandateORM from "@/store/orm-axios/models/Mandate";
import { mandatePresenceStatuses } from "@/constants/mandate.js";
import { meetingMinutesNames } from "@/constants/meeting.js";
import minutesMixin from "@/mixins/minutes";

export default {
  components: {
    appLockOverlay: () => import("@/components/snippets/LockOverlay"),
    appVotingsMandateName: () =>
      import("@/components/b-table-components/votingTable/VotingsMandateName"),
    appVotingsMandateVote: () =>
      import("@/components/b-table-components/votingTable/VotingsMandateVote"),
  },
  mixins: [minutesMixin],
  props: {
    currentResolution: {
      type: Object,
      default: () => {
        return {};
      },
    },
    mandates: {
      type: Array,
      default: () => {
        return {};
      },
    },
    showOverlay: {
      type: Boolean,
      default: false,
    },
    ignoredCorrespondence: {
      type: Boolean,
      default: false,
    },
    isResolutionBlocked: {
      type: Boolean,
      default: true,
    },
    meetingId: {
      type: String,
      required: true,
    },
    minutes: {
      type: Array,
      default: () => {
        return [];
      },
    },
    showMinutesData: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currentResolutionData: {},
      tableData: [],
      fields: [
        {
          label: "Propriétaire",
          key: "building_owner.lastname",
          sortable: true,
          tbClass: "text-left",
        },
        {
          label: this.$t("dt_voting_for_key"),
          key: "for",
          sortable: false,
        },
        {
          label: this.$t("dt_voting_against_key"),
          key: "against",
          sortable: false,
        },
        {
          label: this.$t("dt_voting_abstain_key"),
          key: "abs",
          sortable: false,
        },
        {
          label: "Défaillant",
          key: "nsp",
          sortable: false,
        },
      ],
      search: "",
      mandatePresenceStatuses,
      meetingMinutesNames,
      currentResolutionVoteEvent: null,
    };
  },
  computed: {
    resolutionVotes() {
      return ResolutionVoteORM.getters("getResolutionVotesByResolution")(this.currentResolution.id);
    },
  },
  watch: {
    mandates() {
      this.prepareTableData();
    },
    currentResolution(newVal, oldVal) {
      if (!this.$_.isEqual(newVal, oldVal)) {
        this.currentResolutionData = newVal;
        if (
          !this.isResolutionBlocked &&
          !this.$_.isEqual(newVal.voting_category_id, oldVal.voting_category_id)
        ) {
          this.changeVotesOnCurrentResolutionChange();
        }
        this.prepareTableData();
      }
    },
    resolutionVotes(newVal) {
      if (newVal.length > 0) {
        this.tableData.forEach(mandate => {
          if (mandate.presence_status == this.mandatePresenceStatuses.correspondence) {
            var resolutionVote = ResolutionVoteORM.getters(
              "getResolutionVoteByMandateAndResolution"
            )(mandate.id, this.currentResolution.id);
            this.updateResolutionVote(mandate, resolutionVote);
          }
        });
      }
    },
    showMinutesData(newValue, oldValue) {
      if (newValue != oldValue) {
        this.prepareTableData();
      }
    },
  },
  created() {
    this.prepareTableData();
    this.currentResolutionData = this.currentResolution;
    console.log("correspondence votes table");
  },
  methods: {
    detailsClassFn(item, rowType) {
      let tantiemes = this.getUsedMandateTantiemesByVotingCategory(
        item.id,
        this.currentResolutionData.voting_category_id
      );
      if (item.$disabled) {
        return "bg-secondary not-editable";
      } else if (tantiemes == 0) {
        return "bg-warning-light not-editable";
      }
    },
    sortedMandates(mandates) {
      return this.$_.sortBy(mandates, mandate => {
        return this.getPersonName({ personId: mandate.building_owner_id });
      });
    },
    filter(e) {
      let result = this.$_.filter(this.mandates, mandate => {
        let buildingOwner = this.getPersonName({ personId: mandate.building_owner_id });
        let representantOwner = this.getPersonName({ personId: mandate.representant_owner_id });
        return (
          buildingOwner.toLowerCase().includes(e) || representantOwner.toLowerCase().includes(e)
        );
      });
      this.tableData = this.sortedMandates(result);
    },
    getTantiemes(item) {
      return this.getUsedMandateTantiemesByVotingCategory(
        item.id,
        this.currentResolution.voting_category_id
      );
    },
    disableVoting(disable, tantiemes) {
      if (disable && tantiemes == 0) {
        return true;
      } else if (tantiemes == 0) {
        return true;
      } else {
        return false;
      }
    },
    updateResolutionVote(mandate, resolutionVote) {
      let currentMajority =
        this.currentResolution.revote_majority_id == null
          ? this.currentResolution.majority_id
          : this.currentResolution.revote_majority_id;

      let meetingCorrespondenceVote = MeetingCorrespondenceVoteORM.query()
        .where("mandate_id", mandate.id)
        .first();

      if (meetingCorrespondenceVote) {
        meetingCorrespondenceVote.proposed_votes.forEach(item => {
          if (
            resolutionVote &&
            resolutionVote.meeting_has_resolution_id == item.meeting_resolution_id &&
            resolutionVote.meeting_has_resolution_id == this.currentResolution.id &&
            currentMajority == item.majority_id &&
            resolutionVote.vote != item.vote
          ) {
            ResolutionVoteORM.update({
              where: resolutionVote.id,
              data: {
                vote: item.vote,
              },
            });
          }
        });
      }
    },
    async changeVotesOnCurrentResolutionChange() {
      var mandatesWhichCannotVote = this.$_.filter(this.mandates, mandate => {
        return (
          mandate.presence_status == this.mandatePresenceStatuses.correspondence &&
          this.getUsedMandateTantiemesByVotingCategory(
            mandate.mandate_id ? mandate.mandate_id : mandate.id,
            this.currentResolutionData.voting_category_id
          ) == 0
        );
      });

      mandatesWhichCannotVote.forEach(mandate => {
        let mandateId = mandate.mandate_id ? mandate.mandate_id : mandate.id;
        let meetingCorrespondenceVote = MeetingCorrespondenceVoteORM.query()
          .where("mandate_id", mandateId)
          .first();
        let proposedVotes = meetingCorrespondenceVote.proposed_votes;
        if (proposedVotes && proposedVotes.length > 0) {
          // NOTE: Set all votes from this resolution on null when tantiemes are 0
          let proposedVoteIndex = this.$_.findIndex(proposedVotes, item => {
            return item.meeting_resolution_id == this.currentResolutionData.id;
          });
          proposedVotes[proposedVoteIndex].vote = null;
          MeetingCorrespondenceVoteORM.update({
            where: meetingCorrespondenceVote.id,
            data: {
              proposed_votes: proposedVotes,
            },
          });
          MeetingCorrespondenceVoteMW.PUT(meetingCorrespondenceVote);
        }
      });
    },
    getCurrentResolutionVoteEvent() {
      return this.minutes.find(
        item =>
          item.event == this.meetingMinutesNames.resolution_vote &&
          item.resolution_id == this.currentResolution.id
      );
    },
    prepareTableData() {
      this.currentResolutionVoteEvent = this.getCurrentResolutionVoteEvent();
      if (this.currentResolutionVoteEvent && this.showMinutesData && !this.ignoredCorrespondence) {
        const mandates = this.getCorrespondenceMandates();
        this.tableData = this.sortedMandates(mandates);
      } else {
        this.tableData = this.sortedMandates(this.mandates);
      }
    },
    getCorrespondenceMandates() {
      const minutesMandatesEvents = this.getMinutesMandatesForResolution(this.currentResolution.id);
      const mandates = MandateORM.getters("getMandatesByMeeting")(this.meetingId);

      let correspondenceMandatesToShow = [];
      for (const mandate of mandates) {
        const mandateLastMinuteEvent = this.getMandateLastUpdateFromMinutes(
          minutesMandatesEvents,
          mandate.id
        );

        var resolutionVote = ResolutionVoteORM.getters("getResolutionVoteByMandateAndResolution")(
          mandate.id,
          this.currentResolution.id
        );

        // NOTE: Return only the correspondence events (mandates) that were not changed to other presence types
        if (
          mandateLastMinuteEvent &&
          mandateLastMinuteEvent.new_presence_status &&
          mandateLastMinuteEvent.new_presence_status ==
            this.mandatePresenceStatuses.correspondence &&
          (resolutionVote ? !resolutionVote.ignored_correspondence : true)
        ) {
          let cloneMandate = { ...mandate };
          cloneMandate.presence_status = mandateLastMinuteEvent.new_presence_status;
          correspondenceMandatesToShow.push(cloneMandate);
        }
      }
      return correspondenceMandatesToShow;
    },
  },
};
</script>
